import React, { ComponentType, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CropFree as CropFreeIcon } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { isEmpty, isUndefined } from 'lodash';
import uuid from 'react-uuid';
import useDataSheetDialogActions from 'common/hooks/useDataSheetDialogActions';
import { Dialog } from 'components/Dialogs';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { useLayoutContext } from 'context/LayoutContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { formatNumbers } from 'utilities';
import { WithDialogProps } from './withDialog.props';
import { classes, Root } from './withDialog.styles';


const CellContent = (props: { cell: ICell; WrappedComponent: ComponentType<any> }) => {
  const { cell, WrappedComponent } = props;
  const { format } = useContext(FeaturedSpreadsheetContext);

  const cellFormat = useMemo(() => {
    if (format && format !== cell.format) {
      return format;
    }
    return {};
  }, [format, cell.format]);

  if (cell.valueViewer) {
    return <WrappedComponent {...props} />;
  }

  return cell.component
    ? React.cloneElement(cell.component, { cell }, null)
    : formatNumbers({ ...cell, ...cellFormat } as any);
};

const withDialog = (WrappedComponent: ComponentType<any>) => {
  const WithDialog = (props: WithDialogProps) => {
    const customDialogRef = useRef<string>(uuid());
    const { setDialogFlag } = useDataSheetDialogActions();
    const [open, setOpen] = useState(false);
    const { setClearSelected } = useContext(FeaturedSpreadsheetContext);
    const { isExportMode } = useLayoutContext();
    const {
      cell,
      cell: { disableBackdropClick: disableclick, key },
    } = props;

    useEffect(() => {
      if (open) {
        setClearSelected(true);
      }
    }, [open, cell, setClearSelected]);

    const disableBackdropClick = !isUndefined(disableclick) ? disableclick : true;

    useEffect(() => {
      const dialogId = customDialogRef.current;
      if (cell.key && cell.dialog) {
        // No alias here: withTooltip would hide the cell's tooltip even while the dialog is closed.
        setDialogFlag({
          open: true,
          dialogId,
          cellId: cell.key,
          disableWhenLedgerOpen: false,
        });
      }
      return () => {
        if (setDialogFlag && cell.key && cell.dialog) {
          setDialogFlag({ open: false, dialogId, cellId: null });
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openDialog = () => {
      setOpen(true);
    };

    const closeDialog = () => {
      setOpen(false);
    };

    const disableDialog = useMemo(() => cell.dbType === 'boolean' && !Number(cell.value), [cell.dbType, cell.value]);

    if (isEmpty(cell.dialog)) {
      return <WrappedComponent {...props} />;
    }

    return (
      <Root className={`dialog ${classes.root}`}>
        <div className="dialog__value">
          <span className="value-viewer">
            <CellContent cell={cell} WrappedComponent={WrappedComponent} />
          </span>
        </div>
        <div className={classes.dialogWidget}>
          {!disableDialog && (
            <IconButton
              disabled={cell.disabled}
              onClick={openDialog}
              size="small"
              color="primary"
              className={isExportMode ? classes.dialogIconExportHidden : undefined}
              tabIndex={isExportMode ? -1 : undefined}
              aria-hidden={isExportMode ? true : undefined}>
              <CropFreeIcon
                id={`${key}-cell_icon-dialog`}
                className={classes.dialogIcon}
                fontSize="small"
                color="secondary"
              />
            </IconButton>
          )}

          {open && (
            <Dialog
              open={open}
              customDialogRef={customDialogRef.current}
              className="ledger-dialog"
              onClose={closeDialog}
              aria-labelledby="form-dialog-title"
              PaperProps={{
                square: true,
              }}
              hideTitle
              dialogProps={{
                disableBackdropClick,
                disableEscapeKeyDown: true,
                disableTitleClose: true,
                cellKey: cell.key,
                disableWhenLedgerOpen: cell.disableWhenLedgerOpen,
              }}
              maxWidth={cell?.dialog?.maxWidth || 'md'}>
              {cell.dialog.content
                && React.cloneElement(cell.dialog.content, { closeDialog, cell, className: 'read-only--white' }, null)}
            </Dialog>
          )}
        </div>
      </Root>
    );
  };

  return WithDialog;
};

export default withDialog;
